/**
 * bridgeTokenClient — exchanges the caller's openauth access token for a
 * short-lived, site-scoped sync-bridge token via builder-api's
 * POST /api/v1/sites/:id/bridge-token (mintBridgeToken). The WS sync provider
 * calls this through getToken; the bridge verifies the result against
 * builder-api's public JWKS.
 *
 * Two transports: native sends its openauth token as a Bearer straight to
 * builder-api; the web app has no token in JavaScript (it lives in an
 * HttpOnly session cookie), so it calls its own BFF route with credentials
 * and the server attaches the Bearer.
 *
 * Pure + injectable (fetchImpl/now) so it unit-tests without a network or
 * timers. Caches per siteId and re-mints ~30s before expiry; returns null when
 * there's no auth token (signed-out) or the mint fails, which the provider
 * treats as "stay local-first / retry on reconnect".
 */
export interface BridgeTokenClientOptions {
  /** builder-api origin, e.g. https://builder.daddyshome.fr (no trailing slash). */
  readonly apiBase: string;
  /** Current openauth access token, or null when signed out. Omit when the
   *  session travels as a cookie (the web BFF). */
  readonly getAuthToken?: () => string | null;
  /** Mint route for a site, under `apiBase`. Default: builder-api's
   *  `/api/v1/sites/:id/bridge-token`. */
  readonly path?: (siteId: string) => string;
  /** fetch credentials mode; the web BFF needs "same-origin" for its cookie. */
  readonly credentials?: RequestCredentials;
  readonly fetchImpl?: typeof fetch;
  readonly now?: () => number;
}

export interface BridgeTokenClient {
  getBridgeToken(siteId: string): Promise<string | null>;
}

interface CachedToken {
  readonly token: string;
  readonly expiresAt: number;
}

const SKEW_MS = 30_000;

export function createBridgeTokenClient(
  opts: BridgeTokenClientOptions,
): BridgeTokenClient {
  const fetchFn = opts.fetchImpl ?? fetch;
  const now = opts.now ?? (() => Date.now());
  const cache = new Map<string, CachedToken>();

  return {
    async getBridgeToken(siteId: string): Promise<string | null> {
      const cached = cache.get(siteId);
      if (cached && cached.expiresAt > now() + SKEW_MS) return cached.token;

      let headers: Record<string, string> = {};
      if (opts.getAuthToken) {
        const auth = opts.getAuthToken();
        if (!auth) return null;
        headers = { authorization: `Bearer ${auth}` };
      }

      const path =
        opts.path?.(siteId) ??
        `/api/v1/sites/${encodeURIComponent(siteId)}/bridge-token`;
      const res = await fetchFn(`${opts.apiBase}${path}`, {
        method: "POST",
        headers,
        ...(opts.credentials ? { credentials: opts.credentials } : {}),
      });
      if (!res.ok) return null;

      const json = (await res.json()) as {
        token?: string;
        expiresIn?: number;
      };
      if (!json.token) return null;

      cache.set(siteId, {
        token: json.token,
        expiresAt: now() + (json.expiresIn ?? 300) * 1000,
      });
      return json.token;
    },
  };
}
